import { TableFooter } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import DeleteIcon from "@material-ui/icons/Delete";
import { CategoryDetails_category } from "@saleor/categories/types/CategoryDetails";
import { CollectionDetails_collection } from "@saleor/collections/types/CollectionDetails";
import CardTitle from "@saleor/components/CardTitle";
import Checkbox from "@saleor/components/Checkbox";
import { createRecommendProductReorderHandler } from "@saleor/components/RecommendProductSection/handlers";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import {
  SortableTableBody,
  SortableTableRow
} from "@saleor/components/SortableTable";
import StatusLabel from "@saleor/components/StatusLabel";
import TableCellAvatar, {
  AVATAR_MARGIN
} from "@saleor/components/TableCellAvatar";
import TableHead from "@saleor/components/TableHead";
import { maybe, renderCollection } from "@saleor/misc";
import { useRecommendProductReorderMutation } from "@saleor/products/mutations";
import { ProductDetails_product_recommendProducts } from "@saleor/products/types/ProductDetails";
import { ProductDetails_product } from "@saleor/products/types/ProductDetails";
import { ListActions } from "@saleor/types";
import {
  RecommendationTargetTypeEnum,
  RecommendationTypeEnum
} from "@saleor/types/globalTypes";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    colActions: {
      "&:last-child": {
        paddingRight: 0
      },
      width: 76 + theme.spacing(0.5)
    },
    colName: {
      paddingLeft: 0,
      width: "auto"
    },
    colNameLabel: {
      marginLeft: AVATAR_MARGIN
    },
    colPublished: {
      width: 200
    },
    colType: {
      width: 200
    },
    table: {
      tableLayout: "fixed"
    },
    tableRow: {
      cursor: "pointer"
    },
    colGrab: {
      width: 60
    }
  }),
  { name: "RecommendProductList" }
);

export interface RecommendProductListProps extends ListActions {
  disabled: boolean;
  target:
    | ProductDetails_product
    | CategoryDetails_category
    | CollectionDetails_collection;
  targetType: RecommendationTargetTypeEnum;
  products: ProductDetails_product_recommendProducts[];
  recommendType: RecommendationTypeEnum;
  tabName: string;
  onProductAdd: () => void;
  onProductUnassign: (id: string, event: React.MouseEvent<any>) => void;
  onRowClick: (id: string) => () => void;
}

const numberOfColumns = 6;

const RecommendProductList: React.FC<RecommendProductListProps> = props => {
  const {
    disabled,
    target,
    targetType,
    products,
    recommendType,
    tabName,
    onProductAdd,
    onProductUnassign,
    onRowClick,
    isChecked,
    selected,
    toggle,
    toggleAll,
    toolbar
  } = props;
  const classes = useStyles(props);
  const intl = useIntl();

  const [reorderRecommendProducts] = useRecommendProductReorderMutation({});

  const handleReorder = createRecommendProductReorderHandler(
    target,
    targetType,
    recommendType,
    products,
    variables => reorderRecommendProducts({ variables })
  );

  return (
    <>
      <CardTitle
        title={
          !!tabName ? (
            intl.formatMessage(
              {
                defaultMessage: "Products in {name}",
                description: "recommend products in product"
              },
              {
                name: maybe(() => tabName, "...")
              }
            )
          ) : (
            <Skeleton />
          )
        }
        toolbar={
          <Button
            disabled={disabled}
            variant="text"
            color="primary"
            onClick={onProductAdd}
          >
            <FormattedMessage
              defaultMessage="Assign product"
              description="button"
            />
          </Button>
        }
      />
      <ResponsiveTable className={classes.table}>
        <colgroup>
          <col className={classes.colGrab} />
          <col />
          <col className={classes.colName} />
          <col className={classes.colType} />
          <col className={classes.colPublished} />
          <col className={classes.colActions} />
        </colgroup>
        {products && products.length > 0 ? (
          <>
            <TableHead
              colSpan={numberOfColumns}
              selected={selected}
              disabled={disabled}
              items={products}
              toggleAll={toggleAll}
              toolbar={toolbar}
              dragRows
            >
              <TableCell className={classes.colName}>
                <span className={classes.colNameLabel}>
                  <FormattedMessage
                    defaultMessage="Name"
                    description="product name"
                  />
                </span>
              </TableCell>
              <TableCell className={classes.colType}>
                <FormattedMessage
                  defaultMessage="Default Sku"
                  description="default sku"
                />
              </TableCell>
              <TableCell className={classes.colPublished}>
                <FormattedMessage
                  defaultMessage="Published"
                  description="product is published"
                />
              </TableCell>
              <TableCell className={classes.colActions} />
            </TableHead>
            <SortableTableBody onSortEnd={handleReorder}>
              {renderCollection(products, (product, productIndex) => {
                const isSelected = product ? isChecked(product.id) : false;
                return (
                  <SortableTableRow
                    selected={isSelected}
                    hover={!!product}
                    onClick={onRowClick(product?.id)}
                    key={product ? product.id : "skeleton"}
                    index={productIndex || 0}
                    className={classes.table}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        disableClickPropagation
                        onChange={() => toggle(product.id)}
                      />
                    </TableCell>
                    <TableCellAvatar
                      className={classes.colName}
                      thumbnail={maybe(() => product.thumbnail.url)}
                      data-test="name"
                    >
                      {product ? product.name : <Skeleton />}
                    </TableCellAvatar>
                    <TableCell
                      className={classes.colType}
                      data-test="productType"
                    >
                      {product ? product.defaultVariant?.sku : <Skeleton />}
                    </TableCell>
                    <TableCell className={classes.colPublished}>
                      {maybe(
                        () => (
                          <StatusLabel
                            label={
                              product.isPublished
                                ? intl.formatMessage({
                                    defaultMessage: "Published",
                                    description: "product is published"
                                  })
                                : intl.formatMessage({
                                    defaultMessage: "Not published",
                                    description: "product is not published"
                                  })
                            }
                            status={product.isPublished ? "success" : "error"}
                          />
                        ),
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell className={classes.colActions}>
                      <IconButton
                        disabled={!product}
                        onClick={event => onProductUnassign(product.id, event)}
                      >
                        <DeleteIcon color="primary" />
                      </IconButton>
                    </TableCell>
                  </SortableTableRow>
                );
              })}
            </SortableTableBody>
          </>
        ) : (
          <TableFooter>
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage
                  defaultMessage="Assign products to {name}, use the button above."
                  description="No product found"
                  values={{
                    name: <strong>{tabName}</strong>
                  }}
                />
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </ResponsiveTable>
    </>
  );
};

RecommendProductList.displayName = "RecommendProductList";
export default RecommendProductList;
